import { useState, useEffect, useCallback, useRef } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, Sparkles } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { adGeneratorService } from '@/services/ad-generator'
import apiClient from '@/shared/lib/api'
import type { AdGenJob } from '@/services/ad-generator/types'
import AdGeneratorPipeline from './AdGeneratorPipeline'
import JobPresentation from './components/JobPresentation'

type View = 'loading' | 'pipeline' | 'presentation' | 'empty'

export default function AdGeneratorPage() {
  const [view, setView] = useState<View>('loading')
  const [currentJob, setCurrentJob] = useState<AdGenJob | null>(null)
  const [recentJobs, setRecentJobs] = useState<AdGenJob[]>([])
  const [showJobSelector, setShowJobSelector] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hasInitialized = useRef(false)

  // Check onboarding status first, then load jobs
  const checkOnboardingAndLoadJobs = useCallback(async () => {
    try {
      // First check if there's an onboarding job
      const onboardingResponse = await apiClient.get('/marketing/onboarding/status')
      const onboardingData = onboardingResponse.data

      if (onboardingData.job_id) {
        // Fetch the onboarding job
        const job = await adGeneratorService.getJob(onboardingData.job_id)
        setCurrentJob(job)

        if (job.status === 'completed') {
          setView('presentation')
          // Also load recent jobs for the selector
          loadRecentJobs(false)
          return
        } else if (job.status === 'running' || job.status === 'pending') {
          setView('pipeline')
          return
        }
      }

      // Fallback: load jobs the regular way
      loadRecentJobs(true)
    } catch (err) {
      console.warn('[AdGenerator] Onboarding check failed, falling back to job list:', err)
      loadRecentJobs(true)
    }
  }, [])

  // Load recent jobs on mount and set initial view
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      checkOnboardingAndLoadJobs()
    }
  }, [checkOnboardingAndLoadJobs])

  // Poll for job updates when in pipeline view
  useEffect(() => {
    if (view === 'pipeline' && currentJob && (currentJob.status === 'running' || currentJob.status === 'pending')) {
      pollRef.current = setInterval(() => {
        pollJobStatus(currentJob.job_id)
      }, 3000)

      return () => {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [view, currentJob?.job_id, currentJob?.status])

  const loadRecentJobs = async (isInitialLoad = false) => {
    try {
      const response = await adGeneratorService.listJobs()
      const jobs = response.jobs || []
      setRecentJobs(jobs)

      if (isInitialLoad) {
        // Find the most recent completed job
        const completedJob = jobs.find(j => j.status === 'completed')
        // Or find any running job to resume
        const runningJob = jobs.find(j => j.status === 'running' || j.status === 'pending')

        if (runningJob) {
          setCurrentJob(runningJob)
          setView('pipeline')
        } else if (completedJob) {
          setCurrentJob(completedJob)
          setView('presentation')
        } else {
          setView('empty')
        }
      }
    } catch (err) {
      console.error('[AdGenerator] Failed to load recent jobs:', err)
      setView('empty')
    }
  }

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const job = await adGeneratorService.getJob(jobId)
      setCurrentJob(job)

      // Transition to presentation when complete
      if (job.status === 'completed') {
        if (pollRef.current) clearInterval(pollRef.current)
        setView('presentation')
        loadRecentJobs() // Refresh recent list
      }

      // Stop polling on terminal states
      if (job.status === 'failed' || job.status === 'cancelled') {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    } catch (err) {
      console.error('[AdGenerator] Poll error:', err)
    }
  }, [])

  const handleSelectJob = (job: AdGenJob) => {
    setCurrentJob(job)
    setShowJobSelector(false)
    if (job.status === 'completed') {
      setView('presentation')
    } else if (job.status === 'running' || job.status === 'pending') {
      setView('pipeline')
    } else {
      // Failed or cancelled — show pipeline with error state
      setView('pipeline')
    }
  }

  const handleCancel = async () => {
    if (!currentJob) return
    try {
      await adGeneratorService.cancelJob(currentJob.job_id)
      setCurrentJob(prev => prev ? { ...prev, status: 'cancelled' } : null)
      if (pollRef.current) clearInterval(pollRef.current)
    } catch (err) {
      console.error('[AdGenerator] Cancel error:', err)
    }
  }

  const handleClosePresentation = () => {
    // If there are other completed jobs, stay in presentation with first one
    // Otherwise go to empty state
    const otherCompletedJobs = recentJobs.filter(
      j => j.status === 'completed' && j.job_id !== currentJob?.job_id
    )
    if (otherCompletedJobs.length > 0) {
      setCurrentJob(otherCompletedJobs[0])
    } else {
      setView('empty')
      setCurrentJob(null)
    }
  }

  // Get completed jobs for selector
  const completedJobs = recentJobs.filter(j => j.status === 'completed')

  // Render job selector dropdown
  const renderJobSelector = () => {
    if (completedJobs.length <= 1) return null

    return (
      <div className="absolute top-4 left-4 z-30">
        <div className="relative">
          <button
            onClick={() => setShowJobSelector(!showJobSelector)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg',
              'bg-tf-bg-card/80 backdrop-blur border border-tf-border',
              'text-sm text-tf-text hover:bg-tf-bg-hover transition-colors'
            )}
          >
            <span className="truncate max-w-[200px]">
              {currentJob?.url ? new URL(currentJob.url).hostname : 'Select Job'}
            </span>
            <ChevronDown className={cn('w-4 h-4 transition-transform', showJobSelector && 'rotate-180')} />
          </button>

          {showJobSelector && (
            <div className="absolute top-full left-0 mt-2 w-64 max-h-64 overflow-y-auto rounded-lg bg-tf-bg-card border border-tf-border shadow-lg">
              {completedJobs.map(job => (
                <button
                  key={job.job_id}
                  onClick={() => handleSelectJob(job)}
                  className={cn(
                    'w-full text-left px-4 py-3 text-sm hover:bg-tf-bg-hover transition-colors',
                    job.job_id === currentJob?.job_id && 'bg-tf-bg-hover'
                  )}
                >
                  <div className="font-medium text-tf-text truncate">
                    {job.url ? new URL(job.url).hostname : 'Unknown'}
                  </div>
                  <div className="text-xs text-tf-text-muted mt-0.5">
                    {new Date(job.created_at).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-64px)]">
      {/* Error toast */}
      {error && (
        <div className="mx-4 mt-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-500">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {view === 'loading' && (
        <div className="flex-1 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Animated icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mx-auto mb-6 shadow-lg"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles className="w-10 h-10 text-white" />
              </motion.div>
            </motion.div>

            {/* Loading text */}
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xl font-semibold text-tf-text mb-2"
            >
              Loading Your Ads
            </motion.h3>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-sm text-tf-text-muted mb-6"
            >
              Fetching your ad presentations...
            </motion.p>

            {/* Progress bar */}
            <div className="w-48 h-1.5 bg-tf-bg-muted rounded-full mx-auto overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 2, ease: 'easeInOut' }}
              />
            </div>
          </motion.div>
        </div>
      )}

      {view === 'empty' && (
        <div className="flex-1 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-tf-bg-muted to-tf-bg-card flex items-center justify-center mx-auto mb-6 border border-tf-border"
            >
              <span className="text-4xl">🎬</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-tf-text mb-3"
            >
              No Ads Generated Yet
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-tf-text-muted"
            >
              Complete the marketing onboarding to generate your first ad. Your ad presentations will appear here.
            </motion.p>
          </motion.div>
        </div>
      )}

      {view === 'pipeline' && currentJob && (
        <AdGeneratorPipeline
          job={currentJob}
          onCancel={handleCancel}
        />
      )}

      {view === 'presentation' && currentJob && (
        <div className="relative flex-1">
          {renderJobSelector()}
          <JobPresentation
            job={currentJob}
            onClose={handleClosePresentation}
          />
        </div>
      )}
    </div>
  )
}
